// porp-types is a library for typechecking of props
import PropTypes from "prop-types";
import { memo, useMemo } from "react";

// react-chartjs-2 components
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";

// @mui material components
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import MuiTooltip from "@mui/material/Tooltip";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// ReportsLineChart configurations
import configs from "examples/Charts/LineCharts/ReportsLineChart/configs";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const boxesOverlap = (firstBox, secondBox) =>
  !(
    firstBox.right < secondBox.left ||
    firstBox.left > secondBox.right ||
    firstBox.bottom < secondBox.top ||
    firstBox.top > secondBox.bottom
  );

const pointLabelPlugin = {
  id: "reportsLinePointLabels",
  afterDatasetsDraw(chartInstance) {
    const dataset = chartInstance.data.datasets?.[0];
    const pointLabels = dataset?.pointLabels || [];

    if (!pointLabels.length) {
      return;
    }

    const meta = chartInstance.getDatasetMeta(0);
    const { ctx, chartArea } = chartInstance;
    const placedLabelBoxes = [];
    const labelHeight = 13;
    const labelGap = 8;
    const edgePadding = 4;

    ctx.save();
    ctx.font = "600 10px Roboto, sans-serif";
    ctx.fillStyle = "rgba(255, 255, 255, 0.92)";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    meta.data.forEach((point, index) => {
      const label = pointLabels[index];

      if (!label) {
        return;
      }

      const textWidth = ctx.measureText(label).width;
      const halfWidth = textWidth / 2;
      const x = clamp(
        point.x,
        chartArea.left + halfWidth + edgePadding,
        chartArea.right - halfWidth - edgePadding
      );
      const candidateYs = [
        point.y - labelHeight - labelGap,
        point.y + labelHeight + labelGap,
        point.y - labelHeight * 2 - labelGap,
        point.y + labelHeight * 2 + labelGap,
      ];
      const selectedLabel = candidateYs
        .map((candidateY) => {
          const y = clamp(
            candidateY,
            chartArea.top + labelHeight / 2 + edgePadding,
            chartArea.bottom - labelHeight / 2 - edgePadding
          );

          return {
            x,
            y,
            box: {
              left: x - halfWidth - edgePadding,
              right: x + halfWidth + edgePadding,
              top: y - labelHeight / 2,
              bottom: y + labelHeight / 2,
            },
          };
        })
        .find(({ box }) => !placedLabelBoxes.some((placedBox) => boxesOverlap(box, placedBox)));

      if (!selectedLabel) {
        return;
      }

      placedLabelBoxes.push(selectedLabel.box);
      ctx.fillText(label, selectedLabel.x, selectedLabel.y);
    });

    ctx.restore();
  },
};

const chartPlugins = [pointLabelPlugin];

function ReportsLineChart({
  color,
  title,
  description,
  date,
  chart,
  toolTipMessage,
  valuesHidden,
  valueFormatter,
  pointLabels,
}) {
  const { data, options } = useMemo(() => {
    const maskedChart = valuesHidden
      ? {
          labels: chart.labels || [],
          datasets: {
            ...(chart.datasets || {}),
            data: (chart.datasets?.data || []).map(() => 0),
          },
        }
      : chart;
    const nextActivePointLabels = valuesHidden ? [] : pointLabels;
    const datasets = {
      ...(maskedChart.datasets || {}),
      pointLabels: nextActivePointLabels,
    };
    const configuredChart = configs(maskedChart.labels || [], datasets, {
      valueFormatter: valuesHidden ? undefined : valueFormatter,
      showPointLabels: nextActivePointLabels.length > 0,
    });

    if (valuesHidden) {
      configuredChart.options.plugins.tooltip = { enabled: false };
      configuredChart.options.scales.y.ticks.display = false;
    }

    return configuredChart;
  }, [chart, pointLabels, valueFormatter, valuesHidden]);

  return (
    <Card sx={{ height: "100%" }}>
      <MDBox padding="1rem">
        <MDBox
          variant="gradient"
          bgColor={color}
          borderRadius="lg"
          coloredShadow={color}
          py={2}
          pr={0.5}
          mt={-5}
          height="12.5rem"
        >
          <Line
            data={data}
            options={options}
            plugins={chartPlugins}
          />
        </MDBox>
        <MDBox pt={3} pb={1} px={1}>
          <MDTypography variant="h6" textTransform="capitalize">
            {title}
          </MDTypography>
          <MDTypography component="div" variant="button" color="text" fontWeight="light">
            {description}
          </MDTypography>
          <Divider />
          <MDBox display="flex" alignItems="center">
            <MDTypography variant="button" color="text" lineHeight={1} sx={{ mt: 0.15, mr: 0.5 }}>
              {toolTipMessage && (
                <MuiTooltip title={toolTipMessage}>
                  <Icon>help_outline</Icon>
                </MuiTooltip>
              )}
            </MDTypography>
            <MDTypography variant="button" color="text" fontWeight="light">
              {date}
            </MDTypography>
          </MDBox>
        </MDBox>
      </MDBox>
    </Card>
  );
}

// Setting default values for the props of ReportsLineChart
ReportsLineChart.defaultProps = {
  color: "info",
  description: "",
  valuesHidden: false,
  valueFormatter: undefined,
  pointLabels: [],
  toolTipMessage: undefined,
};

// Typechecking props for the ReportsLineChart
ReportsLineChart.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  title: PropTypes.string.isRequired,
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  date: PropTypes.string.isRequired,
  chart: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.array, PropTypes.object])).isRequired,
  toolTipMessage: PropTypes.string,
  valuesHidden: PropTypes.bool,
  valueFormatter: PropTypes.func,
  pointLabels: PropTypes.arrayOf(PropTypes.string),
};

export default memo(ReportsLineChart);
